import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import {
  AlertTriangle,
  Bell,
  History,
  KeyRound,
  LayoutDashboard,
  type LucideIcon,
  Palette,
  Shield,
  ShieldAlert,
  Timer,
  UserCog,
} from 'lucide-react'
import { useMemo, useSyncExternalStore, type ReactElement } from 'react'

import { MobileSectionSwitcher } from '~/components/navigation/MobileSectionSwitcher'
import { meQuery } from '~/features/me'
import { AuditLogSection } from '~/features/audit-log/components/AuditLogSection'
import {
  AlertsSection,
  AuditRetentionSection,
  BrandSection,
  DangerZoneSection,
  DefaultDashboardsSection,
  IpAllowlistSection,
  MaintenanceModeSection,
  PlatformAdminsSection,
  Require2faSection,
  RolesMatrixSection,
  SlaSection,
} from '~/features/settings'

/**
 * `/admin/settings` — platform-wide settings surface (super_admin only).
 *
 * Spec: `docs/admin-design-refinement/13-profile-and-settings.md` §D.
 * Composition: a sticky 240-wide sub-rail (per HANDOFF §5 invariant 3,
 * `top: 64px`) anchored to each section's `id`, plus the eight section
 * cards rendered in document order so the rail's anchors and the
 * scrollspy-style highlight (CSS `:target` would suffice; we keep it
 * declarative here) work without any JS.
 *
 * Permission gating:
 *   - `meQuery()` resolves the actor's `is_super_admin` flag.
 *   - While loading, render the chrome with skeleton sections.
 *   - On `is_super_admin === false`, swap the canvas for the calm
 *     permission-denied empty state required by §STATES of the spec.
 */
export const Route = createFileRoute('/admin/settings')({
  component: SettingsPage,
})

interface SubRailItem {
  readonly id: string
  readonly label: string
  readonly icon: LucideIcon
  readonly destructive?: boolean
}

const SUB_RAIL: ReadonlyArray<SubRailItem> = [
  { id: 'platform-admins', label: 'Platform admins', icon: Shield },
  { id: 'roles', label: 'Roles & permissions', icon: UserCog },
  { id: 'audit-retention', label: 'Audit-log retention', icon: History },
  { id: 'audit-log', label: 'Audit log viewer', icon: History },
  { id: 'alerts', label: 'Alerts', icon: Bell },
  { id: 'sla', label: 'SLA', icon: Timer },
  { id: 'dashboards', label: 'Default dashboards', icon: LayoutDashboard },
  { id: 'brand', label: 'Brand', icon: Palette },
  { id: 'require-2fa', label: 'Require 2FA', icon: KeyRound },
  { id: 'ip-allowlist', label: 'IP allowlist', icon: Shield },
  { id: 'maintenance-mode', label: 'Maintenance mode', icon: AlertTriangle, destructive: true },
  { id: 'danger', label: 'Danger zone', icon: ShieldAlert, destructive: true },
]

function SettingsPage(): ReactElement {
  const me = useQuery(meQuery())
  const isSuperAdmin = me.data?.is_super_admin === true
  const checking = me.isLoading
  const activeSection = useSettingsSection()

  // Memoize the rail markup so re-renders triggered by individual
  // section query state changes don't re-create the static aside DOM.
  const rail = useMemo(() => <SubRail items={SUB_RAIL} />, [])

  return (
    <div className="ka-page">
      <header className="ka-page-header">
        <div>
          <h1 className="ka-page-header-title">Settings</h1>
          <p className="ka-page-header-subtitle">
            Platform-wide configuration. Restricted to super-admins.
          </p>
        </div>
      </header>

      {checking ? (
        <CheckingPermissionState />
      ) : !isSuperAdmin ? (
        <PermissionDeniedState />
      ) : (
        <div className="ka-settings-layout">
          <MobileSectionSwitcher
            label="Settings section"
            value={activeSection}
            options={SUB_RAIL.map((item) => ({
              value: item.id,
              label: item.label,
            }))}
            onChange={(value) => {
              window.location.hash = value
            }}
          />
          {rail}
          <div className="ka-canvas">
            <PlatformAdminsSection />
            <RolesMatrixSection />
            <AuditRetentionSection />
            <AuditLogSection />
            <AlertsSection />
            <SlaSection />
            <DefaultDashboardsSection />
            <BrandSection />
            <Require2faSection />
            <IpAllowlistSection />
            <MaintenanceModeSection />
            <DangerZoneSection />
          </div>
        </div>
      )}
    </div>
  )
}

function useSettingsSection(): string {
  return useSyncExternalStore(
    (onStoreChange) => {
      window.addEventListener('hashchange', onStoreChange)
      return () => window.removeEventListener('hashchange', onStoreChange)
    },
    () => {
      const current = window.location.hash.slice(1)
      return SUB_RAIL.some((item) => item.id === current)
        ? current
        : (SUB_RAIL[0]?.id ?? '')
    },
    () => SUB_RAIL[0]?.id ?? '',
  )
}

interface SubRailProps {
  readonly items: ReadonlyArray<SubRailItem>
}

/**
 * Sticky settings sub-rail. Each row is a same-page anchor that scrolls
 * smoothly to the section's `id`. We deliberately avoid wiring up a
 * scroll-spy hook here — the visual rail is informational + skip-link
 * for keyboard users; the active section is implied by the anchor click.
 *
 * Uses the kit's `.ka-sub-rail` class for the `top: 64px` invariant.
 */
function SubRail({ items }: SubRailProps): ReactElement {
  return (
    <aside
      className="ka-card ka-sub-rail"
      aria-label="Settings sections"
    >
      {items.map((item) => {
        const Icon = item.icon
        return (
          <a key={item.id} href={`#${item.id}`} className="ka-sub-rail-item">
            <Icon
              size={16}
              aria-hidden="true"
              className={`ka-sub-rail-item-icon${item.destructive ? ' is-destructive' : ''}`}
            />
            <span className="ka-sub-rail-item-label">{item.label}</span>
            {item.destructive ? (
              <span aria-hidden="true" className="ka-danger-dot" />
            ) : null}
          </a>
        )
      })}
    </aside>
  )
}

function CheckingPermissionState(): ReactElement {
  return (
    <div className="ka-card" role="status" aria-live="polite">
      <div className="ka-empty">
        <p className="ka-empty-body">Checking permissions…</p>
      </div>
    </div>
  )
}

function PermissionDeniedState(): ReactElement {
  return (
    <div className="ka-card" role="alert">
      <div className="ka-empty">
        <div className="ka-empty-title">Settings is super-admin only</div>
        <p className="ka-empty-body">
          Platform settings (admin role assignment, audit retention, brand
          configuration, danger zone) are restricted to super-admins. If you
          believe you should have access, contact a current super-admin.
        </p>
        <div>
          <Link
            to="/admin"
            className="ka-btn ka-btn--secondary ka-btn--sm"
          >
            Back to Overview
          </Link>
        </div>
      </div>
    </div>
  )
}
